import { useCallback, useState } from 'react'
import * as API from 'services'
import { useErrorHandling } from 'context/errorHandling'
import FileSaver from 'file-saver'

// Split to multiple states when React version >= 17 (state update batching)
const State = {
  default: () => ({
    pdf: null,
    settings: null,
    pdfLoading: false,
    sendInProgress: false,
    pdfSent: false,
  }),
  pdfLoadStart: (state) => ({ ...state, pdfLoading: true }),
  pdfLoadSuccess: (state, pdf, settings) => ({ ...state, pdfLoading: false, pdf, settings }),
  pdfLoadFailure: (state) => ({ ...state, pdfLoading: false }),
  pdfSendStart: (state) => ({ ...state, sendInProgress: true }),
  pdfSendSuccess: (state, pdf) => ({
    ...state,
    sendInProgress: false,
    pdf,
    pdfSent: true,
  }),
  pdfSendFailure: (state) => ({ ...state, sendInProgress: false }),
}

export const useOrderForwardingSummaryPdf = (orderId) => {
  const [state, setState] = useState(State.default())

  const { handleError } = useErrorHandling()

  const generateOrderForwardingSummaryPdf = useCallback((settings) => {
    setState(State.pdfLoadStart(state))
    const request = {
      sendEmail: false,
      language: settings.language
    }
    API.generateOrderForwardingSummaryPdf(orderId, request)
      .then(pdf => setState(State.pdfLoadSuccess(state, pdf, settings)))
      .catch((error) => {
        handleError(error)
        setState(State.pdfLoadFailure(state))
      })
  }, [orderId, handleError, state])

  const downloadOrderForwardingSummaryPdf = useCallback(() => {
    const pdf = state.pdf
    if (!pdf) {
      return
    }
    FileSaver.saveAs(pdf.data, pdf.filename)
  }, [state.pdf])

  const sendOrderForwardingSummaryPdf = useCallback(() => {
    setState(State.pdfSendStart(state))
    const request = { sendEmail: true, language: state.settings.language }
    API.generateOrderForwardingSummaryPdf(orderId, request)
      .then(pdf => setState(State.pdfSendSuccess(state, pdf)))
      .catch((error) => {
        handleError(error)
        setState(State.pdfSendFailure(state))
      })
  }, [orderId, handleError, state])

  const clearOrderForwardingSummaryPdf = useCallback(() => {
    setState(State.default())
  }, [])

  return {
    isOrderForwardingSummaryPdfReady: !!state.pdf,
    isOrderForwardingSummaryPdfLoading: state.pdfLoading,
    orderForwardingSummaryPdfFilename: state.pdf?.filename,
    isOrderForwardingSummaryPdfSendInProgress: state.sendInProgress,
    isOrderForwardingSummaryPdfSent: state.pdfSent,
    generateOrderForwardingSummaryPdf,
    downloadOrderForwardingSummaryPdf,
    sendOrderForwardingSummaryPdf,
    clearOrderForwardingSummaryPdf
  }
}